<script setup lang="ts">
import ControlCurrentRoute from '@/components/shared/admin/reports/ControlCurrentRoute.vue'
import Graphics from '@/components/shared/admin/reports/Graphics.vue'
import TotalCard from '@/components/shared/admin/reports/TotalCard.vue'
import WorkshopSelector from '@/components/shared/admin/reports/WorkshopSelector.vue'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Title } from '@/components/ui/title'
import { useDownloadReport } from '@/composables/useDownloadReport'
import { formatPrice } from '@/helpers/current-price'
import LightLayout from '@/layouts/LightLayout.vue'
import { Head } from '@inertiajs/vue3'
import { useUrlSearchParams } from '@vueuse/core'
import { FileText, Loader2 } from 'lucide-vue-next'

interface PercentageReport {
    name: string
    total: number
}

interface Workshop {
    id: number
    description: string
    start: string
}

interface CurrentWorkshop {
    id: number
    description: string
}

const props = defineProps<{
    workshops: Workshop[]
    current_workshop: CurrentWorkshop | null
    percentage?: PercentageReport[]
    currency?: {
        name: string
        total: number
        value: number
    }[]
}>()

const colors = ['#1D4ED8', '#FDBA74']

const currentRoute = 'coupons'

const { download_pdf: downloadPdf } = useUrlSearchParams()
const { handleDownload, isGenerating } = useDownloadReport('coupons', props.current_workshop?.id)
</script>

<template>
    <Head title="Relatórios" />
    <div class="bg-slate-50">
        <LightLayout>
            <div class="container">
                <Title v-if="!downloadPdf">Relatórios</Title>
                <div class="mt-4 flex items-center justify-between gap-4" v-if="!downloadPdf">
                    <div class="flex items-center gap-4">
                        <ControlCurrentRoute :current-route="currentRoute" :current-workshop-id="current_workshop?.id ?? null" />
                        <WorkshopSelector :workshops="workshops" :current-workshop-id="current_workshop?.id ?? null" />
                    </div>

                    <div v-if="current_workshop">
                        <Button variant="outline" class="gap-5 rounded-md bg-white" @click="handleDownload" :disabled="isGenerating">
                            <template v-if="!isGenerating"> PDF <FileText class="text-slate-400" /> </template>
                            <template v-else>
                                <Loader2 class="animate-spin" />
                            </template>
                        </Button>
                    </div>
                </div>

                <template v-if="current_workshop">
                    <Title class="mt-6" size="sm">Relatório de cupons</Title>

                    <Card class="mt-4">
                        <CardContent class="grid gap-y-24 p-6 lg:grid-cols-2">
                            <div class="jus col-span-full grid items-center gap-6 px-8 lg:grid-cols-2">
                                <Graphics sublabel="Cupons" title="Total Cupom Percentual" :data="percentage!" :colors="colors"> </Graphics>
                                <div class="grid items-start gap-4 sm:grid-cols-2">
                                    <TotalCard
                                        v-for="item in percentage"
                                        :key="item.name"
                                        :title="`Total ${item.name.toLowerCase()}`"
                                        :value="`${item.total}`"
                                        class="w-full lg:basis-full"
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card class="mt-4" :class="{ 'break-page': downloadPdf }">
                        <CardContent class="grid gap-y-24 p-6 lg:grid-cols-2">
                            <div class="jus col-span-full grid items-center gap-6 px-8 lg:grid-cols-2">
                                <Graphics sublabel="Cupons" title="Total Cupom Fixo" :data="currency!" :colors="colors"> </Graphics>
                                <div>
                                    <div v-for="item in currency" class="grid items-start gap-4">
                                        <div class="gap-y-4">
                                            <h3 class="mb-2 text-lg font-bold">Total {{ item.name.toLowerCase() }}</h3>
                                            <div class="grid items-start gap-4 sm:grid-cols-2">
                                                <TotalCard
                                                    :key="item.name"
                                                    :title="`Quantidade`"
                                                    :value="`${item.total}`"
                                                    class="w-full lg:basis-full"
                                                />
                                                <TotalCard
                                                    :key="item.name"
                                                    :title="`Valor`"
                                                    :value="`${formatPrice(item.value / 100)}`"
                                                    class="w-full lg:basis-full"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </template>

                <Card v-else class="mt-6">
                    <CardContent class="p-6 text-center text-slate-500"> Selecione um evento para visualizar o relatório de cupons. </CardContent>
                </Card>
            </div>
        </LightLayout>
    </div>
</template>
